import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { dryOffDue, expectedCalving, type DomainEvent } from '@dairy/domain';
import { addMonths, animalSchema, birrInputValue, BREED_SUGGESTIONS, newId, tryParseBirr, type AnimalRecord } from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DateField } from '@/components/DateField';
import { IntInput, MoneyInput } from '@/components/inputs';
import { toast } from '@/components/toast';
import { Button, Card, CardTitle, Checkbox, Field, Input, PageHeader, Segmented, Select, Textarea } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { isLocked, saveMany, savePhotoWith, type Change } from '@/db/repo';
import { useAnimals, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { reminderChangesFor } from '@/features/events/effects';
import { PhotoPicker, type PickedPhoto } from './common';

type BreedingNow = 'NOT_SERVED' | 'SERVED' | 'PREGNANT';

/** Screen 7 — Animal form with opening history (AR-1, AR-8; S1-T13). */
export function AnimalFormPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useProfile();
  const settings = useSettings();
  const isOwner = useIsOwner();
  const today = useToday();
  const f = useFormat();
  const animals = useAnimals() ?? [];
  const existing = useLiveQuery(() => (id ? db.animals.get(id) : undefined), [id]);
  const editing = !!id;

  const [tag, setTag] = useState('');
  const [name, setName] = useState('');
  const [sex, setSex] = useState<'FEMALE' | 'MALE'>('FEMALE');
  const [breed, setBreed] = useState<string>(BREED_SUGGESTIONS[0]);
  const [birthDate, setBirthDate] = useState(addMonths(today, -36));
  const [estimated, setEstimated] = useState(false);
  const [damId, setDamId] = useState('');
  const [sireCode, setSireCode] = useState('');
  const [source, setSource] = useState<'OPENING_HERD' | 'PURCHASED' | 'BORN_ON_FARM'>('OPENING_HERD');
  const [purchaseDate, setPurchaseDate] = useState(today);
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<PickedPhoto | null>(null);
  // AR-8 opening history
  const [parity, setParity] = useState('0');
  const [lastCalving, setLastCalving] = useState(addMonths(today, -3));
  const [lastCalvingEstimated, setLastCalvingEstimated] = useState(false);
  const [milkingNow, setMilkingNow] = useState<'MILKING' | 'DRY'>('MILKING');
  const [dryOffDate, setDryOffDate] = useState(addMonths(today, -1));
  const [breedingNow, setBreedingNow] = useState<BreedingNow>('NOT_SERVED');
  const [serviceDate, setServiceDate] = useState(addMonths(today, -2));
  const [checkDate, setCheckDate] = useState(today);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warning, setWarning] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setTag(existing.tag);
    setName(existing.name ?? '');
    setSex(existing.sex);
    setBreed(existing.breed);
    setBirthDate(existing.birthDate);
    setEstimated(existing.birthDateEstimated);
    setDamId(existing.damId ?? '');
    setSireCode(existing.sireCode ?? '');
    setSource(existing.source);
    setPurchaseDate(existing.purchaseDate ?? today);
    setPrice(birrInputValue(existing.purchasePriceSantim));
    setNotes(existing.notes ?? '');
    setParity(String(existing.openingParity));
  }, [existing, today]);

  const canEdit =
    isOwner || (!!existing && existing.source === 'BORN_ON_FARM' && existing.createdBy === profile.userId && !isLocked(existing, profile, settings));
  const females = useMemo(() => animals.filter((a) => a.sex === 'FEMALE' && a.id !== id), [animals, id]);
  const showOpening = !editing && sex === 'FEMALE' && (source === 'OPENING_HERD' || source === 'PURCHASED');
  const parityN = Number(parity) || 0;

  const build = (): { record: Record<string, unknown>; events: Array<Record<string, unknown>> } | null => {
    const errs: Record<string, string> = {};
    const p = tryParseBirr(price);
    const record: Record<string, unknown> = {
      ...(existing ?? {}),
      id: existing?.id ?? newId(),
      tag: tag.trim(),
      name: name.trim() || null,
      sex,
      breed: breed.trim(),
      birthDate,
      birthDateEstimated: estimated,
      damId: damId || null,
      sireCode: sireCode.trim() || null,
      source,
      purchaseDate: source === 'PURCHASED' ? purchaseDate : null,
      purchasePriceSantim: source === 'PURCHASED' && p.ok ? p.santim : null,
      openingParity: sex === 'FEMALE' ? parityN : 0,
      status: existing?.status ?? 'ACTIVE',
      exitDate: existing?.exitDate ?? null,
      exitNote: existing?.exitNote ?? null,
      photoFileId: photo ? photo.id : (existing?.photoFileId ?? null),
      notes: notes.trim() || null,
    };
    const r = animalSchema.safeParse(record);
    if (!r.success) for (const i of r.error.issues) errs[String(i.path[0])] = t('animals.form.invalid');
    if (birthDate > today) errs.birthDate = t('errors.DATE_IN_FUTURE');
    if (birthDate < addMonths(today, -300)) errs.birthDate = t('animals.form.tooOld');
    const dup = animals.find((a) => a.id !== record.id && a.tag.toLowerCase() === tag.trim().toLowerCase());
    if (dup) errs.tag = t('errors.CONFLICT_UNIQUE');
    if (!p.ok) errs.purchasePriceSantim = t('animals.form.invalid');
    const events: Array<Record<string, unknown>> = [];
    if (showOpening) {
      if (parityN < 0 || parityN > 15) errs.parity = t('animals.form.invalid');
      const aid = record.id as string;
      const ev = (type: string, date: string, data: Record<string, unknown>, note?: string) =>
        events.push({ id: newId(), animalId: aid, type, date, data, isOpening: true, costSantim: null, note: note ?? null });
      if (parityN >= 1) {
        if (lastCalving > today || lastCalving < birthDate) errs.lastCalving = t('animals.form.invalidDate');
        ev('CALVING', lastCalving, { calves: [] }, lastCalvingEstimated ? t('animals.form.estimatedNote') : undefined);
        if (milkingNow === 'DRY') {
          if (dryOffDate < lastCalving || dryOffDate > today) errs.dryOffDate = t('animals.form.invalidDate');
          ev('DRY_OFF', dryOffDate, {});
        }
      }
      if (breedingNow !== 'NOT_SERVED') {
        const after = parityN >= 1 ? lastCalving : birthDate;
        if (serviceDate <= after || serviceDate > today) errs.serviceDate = t('animals.form.invalidDate');
        ev('SERVICE', serviceDate, { method: 'AI' });
        if (breedingNow === 'PREGNANT') {
          if (checkDate < serviceDate || checkDate > today) errs.checkDate = t('animals.form.invalidDate');
          ev('PREG_CHECK', checkDate, { result: 'PREGNANT', method: 'PALPATION' });
        }
      }
    }
    setErrors(errs);
    return Object.keys(errs).length ? null : { record, events };
  };

  const softWarning = (): string | null => {
    if (!damId) return null;
    const dam = animals.find((a) => a.id === damId);
    if (dam && birthDate < addMonths(dam.birthDate, 18)) return t('animals.form.youngDam', { tag: dam.tag });
    return null;
  };

  const submit = async (force = false) => {
    const built = build();
    if (!built) return;
    if (!force) {
      const w = softWarning();
      if (w) {
        setWarning(w);
        return;
      }
    }
    setSaving(true);
    try {
      const otherEvents = editing ? ((await db.events.where('animalId').equals(built.record.id as string).toArray()) as unknown as DomainEvent[]) : [];
      const reminders = await reminderChangesFor(built.record as unknown as AnimalRecord, [...otherEvents, ...built.events] as never);
      const changes: Change[] = [
        { entity: 'animal', op: 'upsert', record: built.record },
        ...built.events.map((e): Change => ({ entity: 'event', op: 'upsert', record: e })),
        ...reminders,
      ];
      if (photo) await savePhotoWith({ id: photo.id, kind: 'ANIMAL_PHOTO', blob: photo.blob }, changes);
      else await saveMany(changes);
      toast(t('common.saved'));
      navigate(`/animals/${built.record.id as string}`, { replace: true });
    } catch (e) {
      toast(errorMessage(t, e), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (editing && existing && !canEdit) {
    return <p className="text-stone-700">{t('common.lockedAskOwner')}</p>;
  }

  const exp = breedingNow === 'PREGNANT' ? expectedCalving(serviceDate, settings) : null;

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-8">
      <PageHeader title={editing ? t('animals.form.editTitle') : t('animals.form.newTitle')} />
      <Card className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('animals.tag')} error={errors.tag} hint={t('animals.form.tagHint')}>
            <Input value={tag} onChange={(e) => setTag(e.target.value)} maxLength={20} autoCapitalize="characters" aria-invalid={!!errors.tag} />
          </Field>
          <Field label={t('animals.name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
          </Field>
        </div>
        <Field label={t('animals.sex')}>
          <Segmented
            value={sex}
            onChange={setSex}
            options={[
              { value: 'FEMALE', label: t('animals.female') },
              { value: 'MALE', label: t('animals.male') },
            ]}
          />
        </Field>
        <Field label={t('animals.breed')} error={errors.breed}>
          <Input value={breed} onChange={(e) => setBreed(e.target.value)} list="breeds" maxLength={60} />
          <datalist id="breeds">
            {BREED_SUGGESTIONS.map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
        </Field>
        <Field label={t('animals.birthDate')} error={errors.birthDate}>
          <DateField value={birthDate} max={today} onChange={setBirthDate} />
          <Checkbox label={t('animals.form.estimated')} checked={estimated} onChange={(e) => setEstimated(e.target.checked)} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('animals.dam')} error={errors.damId}>
            <Select value={damId} onChange={(e) => setDamId(e.target.value)}>
              <option value="">{t('common.none')}</option>
              {females.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.tag} {a.name ?? ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('animals.sireCode')}>
            <Input value={sireCode} onChange={(e) => setSireCode(e.target.value)} maxLength={40} />
          </Field>
        </div>
        {source !== 'BORN_ON_FARM' ? (
          <Field label={t('animals.source')}>
            <Segmented
              value={source}
              onChange={(v) => !editing && setSource(v)}
              options={[
                { value: 'OPENING_HERD', label: t('animals.sourceType.OPENING_HERD') },
                { value: 'PURCHASED', label: t('animals.sourceType.PURCHASED') },
              ]}
            />
          </Field>
        ) : null}
        {source === 'PURCHASED' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('animals.purchaseDate')} error={errors.purchaseDate}>
              <DateField value={purchaseDate} max={today} onChange={setPurchaseDate} />
            </Field>
            <Field label={t('animals.purchasePrice')} error={errors.purchasePriceSantim}>
              <MoneyInput value={price} onChange={(e) => setPrice(e.target.value)} />
            </Field>
          </div>
        ) : null}
        <Field label={t('photo.animal')}>
          <PhotoPicker value={photo} onChange={setPhoto} existingFileId={existing?.photoFileId} />
        </Field>
        <Field label={t('animals.notes')}>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
        </Field>
      </Card>

      {showOpening ? (
        <Card className="space-y-4">
          <CardTitle>{t('animals.opening.title')}</CardTitle>
          <p className="text-sm text-stone-600">{t('animals.opening.help')}</p>
          <Field label={t('animals.opening.parity')} error={errors.parity}>
            <IntInput value={parity} onChange={(e) => setParity(e.target.value.replace(/\D/g, ''))} maxLength={2} />
          </Field>
          {parityN >= 1 ? (
            <>
              <Field label={t('animals.opening.lastCalving')} error={errors.lastCalving}>
                <DateField value={lastCalving} max={today} onChange={setLastCalving} />
                <Checkbox label={t('animals.form.estimated')} checked={lastCalvingEstimated} onChange={(e) => setLastCalvingEstimated(e.target.checked)} />
              </Field>
              <Field label={t('animals.opening.milkingNow')}>
                <Segmented
                  value={milkingNow}
                  onChange={setMilkingNow}
                  options={[
                    { value: 'MILKING', label: t('animals.stage.MILKING') },
                    { value: 'DRY', label: t('animals.stage.DRY') },
                  ]}
                />
              </Field>
              {milkingNow === 'DRY' ? (
                <Field label={t('animals.opening.dryOffDate')} error={errors.dryOffDate}>
                  <DateField value={dryOffDate} max={today} onChange={setDryOffDate} />
                </Field>
              ) : null}
            </>
          ) : null}
          <Field label={t('animals.opening.breedingNow')}>
            <Segmented
              value={breedingNow}
              onChange={setBreedingNow}
              options={[
                { value: 'NOT_SERVED', label: t('animals.opening.notServed') },
                { value: 'SERVED', label: t('animals.repro.SERVED') },
                { value: 'PREGNANT', label: t('animals.repro.PREGNANT') },
              ]}
            />
          </Field>
          {breedingNow !== 'NOT_SERVED' ? (
            <Field label={t('animals.opening.serviceDate')} error={errors.serviceDate}>
              <DateField value={serviceDate} max={today} onChange={setServiceDate} />
            </Field>
          ) : null}
          {breedingNow === 'PREGNANT' ? (
            <Field label={t('animals.opening.checkDate')} error={errors.checkDate}>
              <DateField value={checkDate} max={today} onChange={setCheckDate} />
            </Field>
          ) : null}
          {exp ? <p className="rounded-xl bg-green-50 p-3 text-green-900">{t('animals.opening.expectedCalving', { date: f.date(exp), dryOff: f.date(dryOffDue(exp, settings)) })}</p> : null}
        </Card>
      ) : null}
      {editing ? <p className="text-sm text-stone-600">{t('animals.form.editOpeningHint')}</p> : null}

      <Button size="lg" className="w-full" disabled={saving} onClick={() => void submit()}>
        {t('common.save')}
      </Button>
      <ConfirmDialog open={!!warning} onOpenChange={(o) => !o && setWarning(null)} title={t('common.areYouSure')} confirmLabel={t('common.saveAnyway')} onConfirm={() => submit(true)}>
        <p>{warning}</p>
      </ConfirmDialog>
    </div>
  );
}
